/**
 * Get the first two initials of a full name and convert them to uppercase
 * Handles both "First Last" and "Last, First" formats
 * @param {string} name - The full name or email address to get the initials of
 * @returns {string} The first two initials of the full name in uppercase
 * @example
 * getInitials('John Doe') // 'JD'
 * getInitials('Doe, John') // 'JD'
 * getInitials('john.doe@example.com') // 'JD'
 */
export function getInitials(name: string): string {
  const trimmedName = name.trim();

  if (trimmedName.includes('@')) {
    const [localPart] = trimmedName.split('@');
    const parts = localPart.split('.');
    const initials = parts
      .slice(0, 2)
      .map((n) => n[0])
      .join('');
    return initials.toUpperCase();
  }

  if (trimmedName.includes(',')) {
    const [last, first] = trimmedName.split(',').map((part) => part.trim());
    const firstInitial = first[0] ?? '';
    const lastInitial = last[0] ?? '';
    return (firstInitial + lastInitial).toUpperCase();
  }

  const parts = trimmedName.split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((n) => n[0])
    .join('');
  return initials.toUpperCase();
}

/**
 * Get the first name of a full name or email
 * Handles both "First Last" and "Last, First" formats
 * @param {string} name - The full name or email to get the first name of
 * @returns {string} The first name of the full name or email
 * @example
 * getFirstName('John Doe') // 'John'
 * getFirstName('Doe, John') // 'John'
 * getFirstName('john.doe@example.com') // 'john'
 */
export function getFirstName(name: string): string {
  const trimmedName = name.trim();

  if (trimmedName.includes('@')) {
    return trimmedName.split('@')[0].split('.')[0];
  }

  if (trimmedName.includes(',')) {
    // Handle "Last, First" format
    const [, first] = trimmedName.split(',').map((part) => part.trim());
    return first;
  }

  // Handle "First Last" format
  return trimmedName.split(' ')[0];
}
